import { useQuery } from "@tanstack/react-query";
import { Button } from "@workspace/web-ui/components/button";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@workspace/web-ui/components/dialog";
import { Select, SelectContent, SelectGroup, SelectItem, SelectTrigger, SelectValue } from "@workspace/web-ui/components/select";
import { cn } from "@workspace/web-ui/lib/utils";
import { Check, Circle, Info, Loader2, X, ChevronDown, ChevronUp, Plus } from "lucide-react";
import { useState } from "react";
import { toast } from "sonner";
import { MedicalRefillsComponent } from "~/features/dashboard/components/refills";
import { getRouteApi } from "@tanstack/react-router";
import { FormattedEncounter } from "../../types";
import { getRefillsByIdOptions } from "../../api/queries/refills";
import { PatientCard } from "~/features/dashboard/components/refills/patient-card";
import { RefillCard } from "~/features/dashboard/components/refills/refill-card";
import { useApproveRefillMutation, useRejectRefillMutation } from "~/features/dashboard/api/refills";
import { useSendInvoiceMutation } from "../../api/mutations";
import DosespotButton from "~/features/dashboard/pages/patient-logs/pages/dosespotModal";
import { formatDate } from "~/utils";
import { ImagesDocsSection, ImagesDocsSectionWhite } from "~/features/dashboard/components/refills/images-docs-section";
import { PrescriptionModal } from "../../../patient-logs/components/modals/prescription-modal";

interface ImageModalProps {
    isOpen: boolean;
    onClose: () => void;
    imageUrl: string;
    imageIndex: number;
    totalImages: number;
}

const ImageModal = ({ isOpen, onClose, imageUrl, imageIndex, totalImages }: ImageModalProps) => {
    return (
        <Dialog open={isOpen} onOpenChange={onClose}>
            <DialogContent className="max-w-4xl w-full max-h-[90vh] xl:max-h-[95vh] p-0">
                <DialogHeader className="p-6 pb-0">
                    <DialogTitle className="text-lg font-semibold">
                        Image {imageIndex + 1} of {totalImages}
                    </DialogTitle>
                </DialogHeader>
                <div className="p-6 pt-4">
                    <div className="relative">
                        <img
                            src={imageUrl}
                            alt={`Encounter Image ${imageIndex + 1}`}
                            className="w-full h-auto max-h-[70vh] object-contain rounded-md"
                        />
                    </div>
                </div>
            </DialogContent>
        </Dialog>
    );
};

export const EncounterMedicationRefillAction = ({ encounter }: { encounter: FormattedEncounter }) => {
    console.log("encounter", encounter)
    const { data: medication, isLoading, isError } = useQuery(getRefillsByIdOptions({
        id: encounter.id ?? ""
    }))
    const routeApi = getRouteApi("/(dashboard)/dashboard/$program");
    const { program } = routeApi.useParams();
    const [isOpen, setIsOpen] = useState(false);

    // Reject modal state
    const [showRejectModal, setShowRejectModal] = useState(false);
    const [rejectReason, setRejectReason] = useState("");
    const [rejectDetails, setRejectDetails] = useState("");
    const [reasonError, setReasonError] = useState("");

    // Image modal state
    const [selectedImage, setSelectedImage] = useState<{ url: string; index: number } | null>(null);
    const [expandedRefills, setExpandedRefills] = useState<Set<string>>(new Set());
    const [rejectingRefillId, setRejectingRefillId] = useState<string | null>(null);
    const [rejectingMedication, setRejectingMedication] = useState<{ name: string; dosage: string } | null>(null);
    const [openPrescriptionModal, setOpenPrescriptionModal] = useState(false);

    // Mutations
    const approveRefillMutation = useApproveRefillMutation({
        refill_id: medication?.id ?? ""
    });

    const rejectRefillMutation = useRejectRefillMutation();

    const sendInvoiceMutation = useSendInvoiceMutation();

    const handleImageClick = (imageUrl: string, index: number) => {
        setSelectedImage({ url: imageUrl, index });
    };

    const handleCloseImageModal = () => {
        setSelectedImage(null);
    };

    const handleAddMedication = () => {
        setOpenPrescriptionModal(true);
    };

    // Determine if "Send to Pharmacy" button should be disabled based on tasks
    const shouldDisableSendToPharmacy = () => {
        const tasks = encounter?.recommendedTasks ?? [];
        
        // If there are no tasks, button should be enabled
        if (!Array.isArray(tasks) || tasks.length === 0) {
            return false;
        }
        
        // If there are tasks, check if any are completed (paid for)
        const hasCompletedTask = tasks.some((task: any) => task?.status === "completed");
        
        // If all tasks are pending (none completed), disable the button
        // If at least one task is completed, enable the button
        return !hasCompletedTask;
    };

    // Check if any task has is_erx_sent === true to determine button text
    const shouldShowResendText = () => {
        const tasks = encounter?.recommendedTasks ?? [];
        return Array.isArray(tasks) && tasks.length > 0 && tasks.some((task: any) => task?.is_erx_sent === true);
    };

    const handleApproveRefill = async () => {
        try {
            // const response = await approveRefillMutation.mutateAsync();

            if (medication?.status === "approved" || medication?.status === "requested") {
                // If it's weight-loss program and user has paid for the refill, send invoice to pharmacy
                if (program === "weight-loss" && medication && medication.paymentStatus === "paid") {
                    try {
                        await sendInvoiceMutation.mutateAsync({
                            patient_id: medication.patient.user_id ?? "",
                            type_id: medication.id ?? "",
                            type: "Refill"
                        });
                    } catch (invoiceError) {
                        // Don't fail the whole operation if invoice sending fails
                    }
                }

                toast.success('Refill Request Approved Successfully', {
                    style: {
                        background: "#10B981",
                        color: "#FFFFFF",
                        borderColor: "#10B981",
                        '& svg': {
                            color: '#FFFFFF'
                        }
                    } as React.CSSProperties
                });
            }
        } catch (error) {
            return toast.error(
                'Failed to approve refill request.',
                {
                    style: {
                        background: '#DC2626',
                        color: '#FFFFFF',
                        borderColor: '#DC2626',
                        '& svg': {
                            color: '#FFFFFF'
                        }
                    } as React.CSSProperties
                }
            );
        }
    };

    const handleRejectRefill = async (reason: string, details: string, specificRefillId?: string) => {
        try {
            const targetRefillId = specificRefillId ?? medication?.id ?? "";
            const response = await rejectRefillMutation.mutateAsync({
                refill_id: targetRefillId,
                reason,
                details,
            });

            if (response.status === "success") {
                toast.success('Refill Request Rejected Successfully', {
                    style: {
                        background: "#10B981",
                        color: "#FFFFFF",
                        borderColor: "#10B981",
                        '& svg': {
                            color: '#FFFFFF'
                        }
                    } as React.CSSProperties
                });
                
                // If not rejecting a specific refill, close the modal
                if (!specificRefillId) {
                    setShowRejectModal(false);
                }
            }
        } catch (error) {
            return toast.error(
                'Failed to reject refill request.',
                {
                    style: {
                        background: '#DC2626',
                        color: '#FFFFFF',
                        borderColor: '#DC2626',
                        '& svg': {
                            color: '#FFFFFF'
                        }
                    } as React.CSSProperties
                }
            );
        }
    };

    const toggleRefillExpansion = (refillId: string) => {
        setExpandedRefills(prev => {
            // If clicking on an already expanded refill, close it
            if (prev.has(refillId)) {
                return new Set();
            }
            // Otherwise, close all others and open only this one (accordion behavior)
            return new Set([refillId]);
        });
    };

    const openRejectModal = (refillId?: string, medication?: { name: string; dosage: string }) => {
        setRejectingRefillId(refillId || null);
        setRejectingMedication(medication || null);
        setShowRejectModal(true);
    };

    const closeRejectModal = () => {
        setShowRejectModal(false);
        setRejectingRefillId(null);
        setRejectingMedication(null);
        setRejectReason("");
        setRejectDetails("");
        setReasonError("");
    };

    if (isLoading) {
        return <Loader2 className="animate-spin duration-1000 text-blue-700 flex justify-center items-center" />
    }

    if (isError) {
        return null
    }

    return (
        <div className="flex flex-col gap-4 ml-4 h-full bg-[#FFFFFF] rounded-[24px] border border-[#0404041A] p-4 sm:py-[16px] sm:px-[32px]">
            <h2 className="text-xl font-medium">Medication Refill Request</h2>

            <div className="flex flex-row gap-2 items-center mt-3 justify-between w-full">
                <div className="flex flex-col gap-2 w-full">
                    <h2 className="text-3xl font-bold">{medication?.userName}</h2>

                    <PatientCard
                        patientName={medication?.patient.name ?? ""}
                        patientEmail={medication?.patient.email ?? ""}
                        programEnrollment={program ?? ""}
                        requestDate={`${formatDate(medication?.requestedDate ?? "")}`}
                        currentWeight={`${medication?.weightHistory?.currentWeight ?? ""} lbs`}
                        patientId={medication?.patient.user_id ?? ""}
                    />

                    {/* Render batch refills with collapsible cards or single refill */}
                    {medication?.isBatch && Array.isArray(medication?.medications) ? (
                        // Batch refills - collapsible cards
                        <div className="space-y-3">
                            {medication.medications.map((med: any, index: number) => {
                                const isExpanded = expandedRefills.has(med.refillId || `refill-${index}`);
                                const medImageUrls = med.imageUrls || [];
                                const medFileUrls = med.fileUrls || [];

                                return (
                                    <div key={med.refillId || index} className="border rounded-lg overflow-hidden bg-white">
                                        {/* Collapsible Header */}
                                        <button
                                            onClick={() => toggleRefillExpansion(med.refillId || `refill-${index}`)}
                                            className="w-full p-4 flex items-center justify-between hover:bg-gray-50 transition-colors"
                                        >
                                            <div className="flex-1 text-left">
                                                <div className="flex items-center gap-2">
                                                    <span className="text-sm font-medium text-gray-500">Medication {index + 1}</span>
                                                </div>
                                                <h3 className="text-md font-medium text-gray-900 mt-1">{med.name}</h3>
                                            </div>
                                            <div className="ml-4">
                                                {isExpanded ? (
                                                    <ChevronUp className="w-5 h-5 text-gray-500" />
                                                ) : (
                                                    <ChevronDown className="w-5 h-5 text-gray-500" />
                                                )}
                                            </div>
                                        </button>

                                        {/* Collapsible Content */}
                                        {isExpanded && (
                                            <div className="px-4 pb-4 border-t bg-gray-50">
                                                {/* Medication Details */}
                                                <div className="mt-4">
                                                    <RefillCard
                                                        medicationName={med.name}
                                                        medicationDosage={med.dosage}
                                                        medicationQuantity={med.refills?.toString() || ''}
                                                        medicationVitamin={""}
                                                    />
                                                </div>

                                                {/* Images and Files for Urgent Care */}
                                                {program === "urgent-care" && (
                                                    <ImagesDocsSectionWhite
                                                        imageUrls={medImageUrls}
                                                        fileUrls={medFileUrls}
                                                        onImageClick={handleImageClick}
                                                    />
                                                )}

                                                {/* Side Effects for Weight Loss */}
                                                {program === "weight-loss" && medication?.sideEffects && (
                                                    <div className="w-full mt-4">
                                                        <div className="bg-white rounded-lg p-4 min-h-[200px] flex flex-col">
                                                            <h3 className="text-lg font-semibold mb-4">Side Effects</h3>
                                                            <div className="flex-1 space-y-3">
                                                                {(() => {
                                                                    const sideEffects = medication.sideEffects as Record<string, any>;
                                                                    const reportedEffects: string[] = [];

                                                                    const effectLabels: Record<string, string> = {
                                                                        nausea: 'Nausea',
                                                                        diarrhea: 'Diarrhea',
                                                                        constipation: 'Constipation',
                                                                        headache: 'Headache',
                                                                        none: 'None'
                                                                    };

                                                                    Object.keys(effectLabels).forEach(key => {
                                                                        if (sideEffects[key] === true && effectLabels[key]) {
                                                                            reportedEffects.push(effectLabels[key]);
                                                                        }
                                                                    });

                                                                    const hasReportedEffects = reportedEffects.length > 0;
                                                                    const hasOtherSymptoms = sideEffects.other_symptoms && sideEffects.other_symptoms.trim() !== '';

                                                                    if (!hasReportedEffects && !hasOtherSymptoms) {
                                                                        return (
                                                                            <div className="flex-1 flex flex-col items-center justify-center text-gray-400 py-6">
                                                                                <span className="text-3xl mb-2">📋</span>
                                                                                <span className="text-sm text-center px-2">No side effects reported</span>
                                                                            </div>
                                                                        );
                                                                    }

                                                                    return (
                                                                        <>
                                                                            {hasReportedEffects && (
                                                                                <div>
                                                                                    <p className="text-sm font-medium text-gray-700 mb-2">Reported Symptoms:</p>
                                                                                    <div className="flex flex-wrap gap-2 bg-white rounded-md p-1 border border-gray-200">
                                                                                        {reportedEffects.map((effect, idx) => (
                                                                                            <li
                                                                                                key={idx}
                                                                                                className="px-3 py-1.5 rounded-full text-sm list-disc list-inside"
                                                                                                style={{ display: 'list-item' }}
                                                                                            >
                                                                                                {effect}
                                                                                            </li>
                                                                                        ))}
                                                                                    </div>
                                                                                </div>
                                                                            )}

                                                                            {hasOtherSymptoms && (
                                                                                <div>
                                                                                    <p className="text-sm font-medium text-gray-700 mb-2">Additional Symptoms:</p>
                                                                                    <div className="bg-white rounded-md py-2 px-4 border border-gray-200">
                                                                                        <ul className="list-disc list-inside space-y-1">
                                                                                            {sideEffects.other_symptoms
                                                                                                .split('\n')
                                                                                                .map((symptom: string, idx: number) => {
                                                                                                    const trimmed = symptom.trim();
                                                                                                    if (!trimmed) return null;
                                                                                                    return (
                                                                                                        <li key={idx}>
                                                                                                            <span className="text-sm text-gray-700 whitespace-pre-wrap">{trimmed}</span>
                                                                                                        </li>
                                                                                                    );
                                                                                                })}
                                                                                        </ul>
                                                                                    </div>
                                                                                </div>
                                                                            )}
                                                                        </>
                                                                    );
                                                                })()}
                                                            </div>
                                                        </div>
                                                    </div>
                                                )}

                                                {/* Reject button for this specific refill */}
                                                <div className="mt-4 pt-4 border-t">
                                                    <Button
                                                        className="w-full !bg-[#E12121] !py-[10px] px-[16px] !rounded-[8px]"
                                                        onClick={(e) => {
                                                            e.stopPropagation();
                                                            openRejectModal(med.refillId, { name: med.name, dosage: med.dosage });
                                                        }}
                                                    >
                                                        Reject This Refill
                                                    </Button>
                                                </div>
                                            </div>
                                        )}
                                    </div>
                                );
                            })}
                        </div>
                    ) : (
                        // Single refill - non-collapsible
                        <>
                            <RefillCard
                                medicationName={!Array.isArray(medication?.medications) ? medication?.medications.medication ?? "" : ""}
                                medicationDosage={!Array.isArray(medication?.medications) ? medication?.medications.medicationDosage ?? "" : ""}
                                medicationQuantity={!Array.isArray(medication?.medications) ? medication?.medications.refills?.toString() || '' : ''}
                                medicationVitamin={""}
                            />
                        </>
                    )}
                </div>
            </div>

            {/* Conditional rendering based on program type - Only for single refills */}
            {!medication?.isBatch && program === "urgent-care" ? (
                /* Lab Documents and Images Grid for Urgent Care */
                <ImagesDocsSection
                    imageUrls={medication?.imageUrls || []}
                    fileUrls={medication?.fileUrls || []}
                    onImageClick={handleImageClick}
                />
            ) : !medication?.isBatch && program === "weight-loss" ? (
                /* Side Effects Section for Weight Loss */
                <div className="w-full mt-6">
                    <div className="bg-gray-50 rounded-lg p-4 min-h-[200px] flex flex-col">
                        <h3 className="text-lg font-semibold mb-4">Side Effects</h3>
                        {medication?.sideEffects ? (
                            <div className="flex-1 space-y-3">
                                {(() => {
                                    const sideEffects = medication.sideEffects as Record<string, any>;
                                    const reportedEffects: string[] = [];

                                    // Map of side effect keys to display names
                                    const effectLabels: Record<string, string> = {
                                        nausea: 'Nausea',
                                        diarrhea: 'Diarrhea',
                                        constipation: 'Constipation',
                                        headache: 'Headache',
                                        none: 'None'
                                    };

                                    // Collect all true side effects
                                    Object.keys(effectLabels).forEach(key => {
                                        if (sideEffects[key] === true && effectLabels[key]) {
                                            reportedEffects.push(effectLabels[key]);
                                        }
                                    });

                                    const hasReportedEffects = reportedEffects.length > 0;
                                    const hasOtherSymptoms = sideEffects.other_symptoms && sideEffects.other_symptoms.trim() !== '';

                                    if (!hasReportedEffects && !hasOtherSymptoms) {
                                        return (
                                            <div className="flex-1 flex flex-col items-center justify-center text-gray-400 py-6">
                                                <span className="text-3xl mb-2">📋</span>
                                                <span className="text-sm text-center px-2">No side effects reported</span>
                                            </div>
                                        );
                                    }

                                    return (
                                        <>
                                            {hasReportedEffects && (
                                                <div>
                                                    <p className="text-sm font-medium text-gray-700 mb-2">Reported Symptoms:</p>
                                                    <div className="flex flex-wrap gap-2 bg-white rounded-md p-1 border border-gray-200">
                                                        {reportedEffects.map((effect, idx) => (
                                                            <li
                                                                key={idx}
                                                                className="px-3 py-1.5 rounded-full text-sm list-disc list-inside"
                                                                style={{ display: 'list-item' }}
                                                            >
                                                                {effect}
                                                            </li>
                                                        ))}
                                                    </div>
                                                </div>
                                            )}

                                            {hasOtherSymptoms && (
                                                <div>
                                                    <p className="text-sm font-medium text-gray-700 mb-2">Additional Symptoms:</p>
                                                    <div className="bg-white rounded-md py-2 px-4 border border-gray-200">
                                                        <ul className="list-disc list-inside space-y-1">
                                                            {sideEffects.other_symptoms
                                                                .split('\n')
                                                                .map((symptom: string, idx: number) => {
                                                                    const trimmed = symptom.trim();
                                                                    if (!trimmed) return null;
                                                                    return (
                                                                        <li key={idx}>
                                                                            <span className="text-sm text-gray-700 whitespace-pre-wrap">{trimmed}</span>
                                                                        </li>
                                                                    );
                                                                })}
                                                        </ul>
                                                    </div>
                                                </div>
                                            )}
                                        </>
                                    );
                                })()}
                            </div>
                        ) : (
                            <div className="flex-1 flex flex-col items-center justify-center text-gray-400 py-6">
                                <span className="text-3xl mb-2">📋</span>
                                <span className="text-sm text-center px-2">No side effects reported</span>
                            </div>
                        )}
                    </div>
                </div>
            ) : null}

            {/* Assigned Actions Section */}
            {(() => {
                const tasks = encounter?.recommendedTasks ?? [];
                const hasAssignedActions = Array.isArray(tasks) && tasks.length > 0;
                const isRejected = medication?.status === "rejected";

                const humanize = (s?: string) =>
                    s ? s.replace(/_/g, " ").replace(/\b\w/g, (c) => c.toUpperCase()) : "Task";

                const getTaskTitle = (task: any) => {
                    if (task?.type === "medication_payment") return "Prescribed Medication";
                    if (task?.type === "appointment_followup") return "Pay for Appointment";
                    return humanize(task?.type);
                };

                const formatActionDate = (d?: string | number) => {
                    if (!d) return "-";
                    const dt = new Date(d);
                    if (isNaN(dt.getTime())) return "-";
                    return dt.toLocaleDateString("en-GB", {
                        day: "numeric",
                        month: "long",
                        year: "numeric",
                    });
                };

                return (
                    <div className="flex flex-col gap-3 w-full text-left rounded-[16px] border border-[#0404041A] pb-4 px-4 pt-4 mt-6">
                                <div className="flex items-center justify-between w-full">
                                    <h2 className="font-medium text-[#1A202C]">
                                        {program === "urgent-care" ? "Assigned actions" : "Recommended actions"}
                                    </h2>
                                    {program === "weight-loss" && (
                                        <Button
                                            className="text-white bg-[#4272DD] w-fit rounded-[8px] shadow-none border border-none hover:bg-[#4272DD]"
                                            onClick={handleAddMedication}
                                        >
                                            <Plus className="w-4 h-4 mr-2" />
                                            <p>Add medication</p>
                                        </Button>
                                    )}
                                </div>

                        {hasAssignedActions || isRejected ? (
                            <div className="space-y-4">
                                {/* Show rejected refill as an assigned action */}
                                {isRejected && medication?.rejectionReason && (
                                    <div className="bg-[#F7F8FA] rounded-[8px] p-4">
                                        <div className="flex items-start justify-between mb-3">
                                            <div className="flex items-center gap-3">
                                                <div className="w-5 h-5 rounded-full bg-red-500 flex items-center justify-center">
                                                    <X className="w-3 h-3 text-white" />
                                                </div>
                                                <div>
                                                    <h3 className="font-medium text-red-600">
                                                        Rejected Refill Request
                                                    </h3>
                                                    <p className="text-sm text-[#6B7280]">
                                                        Rejected: {formatActionDate(medication?.requestedDate)}
                                                    </p>
                                                </div>
                                            </div>
                                        </div>
                                        <div className="bg-white rounded-md p-2 space-y-2">
                                            <h4 className="font-medium text-[#1A202C]">
                                                {(medication?.rejectionReason as any)?.reason || "Rejection Reason"}
                                            </h4>
                                            <div className="grid grid-cols-1 md:grid-cols-2 gap-4 text-sm text-[#6B7280]">
                                                {medication?.isBatch && Array.isArray(medication?.medications) ? (
                                                    // Show all medications for batch
                                                    medication.medications.map((med: any, idx: number) => (
                                                        <div key={idx} className="md:col-span-2 border-b pb-2 mb-2">
                                                            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                                                                <div>
                                                                    <span className="font-medium">Medication: </span>
                                                                    <span className="line-through text-red-600">
                                                                        {med?.name ?? "-"}
                                                                    </span>
                                                                </div>
                                                                <div>
                                                                    <span className="font-medium">Dosage: </span>
                                                                    {med?.dosage ?? "-"}
                                                                </div>
                                                                <div>
                                                                    <span className="font-medium">Refills: </span>
                                                                    {med?.refills ?? "-"}
                                                                </div>
                                                            </div>
                                                        </div>
                                                    ))
                                                ) : (
                                                    <>
                                                        <div>
                                                            <span className="font-medium">Medication: </span>
                                                            <span className="line-through text-red-600">
                                                                {!Array.isArray(medication?.medications) ? medication?.medications?.medication ?? "-" : "-"}
                                                            </span>
                                                        </div>
                                                        <div>
                                                            <span className="font-medium">Dosage: </span>
                                                            {!Array.isArray(medication?.medications) ? medication?.medications?.medicationDosage ?? "-" : "-"}
                                                        </div>
                                                        <div>
                                                            <span className="font-medium">Refills: </span>
                                                            {!Array.isArray(medication?.medications) ? medication?.medications?.refills ?? "-" : "-"}
                                                        </div>
                                                    </>
                                                )}
                                                <div className="md:col-span-2">
                                                    <span className="font-medium">Rejection Details: </span>
                                                    <span>{(medication?.rejectionReason as any)?.details ?? "-"}</span>
                                                </div>
                                                <div>
                                                    <span className="font-medium">Request Date: </span>
                                                    {formatActionDate(medication?.requestedDate)}
                                                </div>
                                            </div>
                                        </div>
                                    </div>
                                )}

                                {/* Show recommended tasks */}
                                {tasks.map((task: any, index: number) => {
                                    const isMedicationTask = task?.type === "medication_payment";
                                    const meds = task?.prescription?.medication ?? [];
                                    const isTaskCompleted = task?.status === "completed";
                                    const isTaskPending = task?.status === "pending";
                                    const hasPrescription = task?.prescription && meds.length > 0;
                                    // Only show green if task is completed (not pending) and has prescription for medication tasks
                                    const showGreen = isMedicationTask 
                                        ? (hasPrescription && !isTaskPending && isTaskCompleted)
                                        : isTaskCompleted;

                                    return (
                                        <div key={task?.task_id || index} className="bg-[#F7F8FA] rounded-[8px] p-4">
                                            <div className="flex items-start justify-between mb-3">
                                                <div className="flex items-center gap-3">
                                                    {showGreen ? (
                                                        <div className="w-5 h-5 rounded-full bg-green-500 flex items-center justify-center">
                                                            <Check className="w-3 h-3 text-white" />
                                                        </div>
                                                    ) : (
                                                        <Circle className="w-5 h-5 text-blue-500" />
                                                    )}
                                                    <div>
                                                        <h3
                                                            className={cn(
                                                                "font-medium",
                                                                showGreen ? "text-green-600 line-through" : "text-[#1A202C]"
                                                            )}
                                                        >
                                                            {getTaskTitle(task)}
                                                        </h3>
                                                        <p className="text-sm text-[#6B7280]">
                                                            Assigned: {formatActionDate(task?.created_at)}
                                                        </p>
                                                    </div>
                                                </div>
                                            </div>

                                            {/* Medication details */}
                                            {isMedicationTask && Array.isArray(meds) && meds.length > 0 && (
                                                <div className="space-y-3">
                                                    {meds.map((m: any, i: number) => (
                                                        <div
                                                            key={`${m?.name || "med"}-${i}`}
                                                            className="bg-white rounded-md p-2 space-y-2"
                                                        >
                                                            <h4 className="font-medium text-[#1A202C]">
                                                                {m?.name || "-"}
                                                            </h4>
                                                            <div className="grid grid-cols-1 md:grid-cols-2 gap-4 text-sm text-[#6B7280]">
                                                                <div>
                                                                    <span className="font-medium">Dosage: </span>
                                                                    {m?.dosage ?? "-"}
                                                                </div>
                                                                <div>
                                                                    <span className="font-medium">Duration: </span>
                                                                    {typeof m?.duration === "number" ? `${m.duration} days` : (m?.duration ?? "-")}
                                                                </div>
                                                                <div className="md:col-span-2">
                                                                    <span className="font-medium">Instructions: </span>
                                                                    {Array.isArray(m?.instructions) ? (
                                                                        <ul className="list-disc ml-5">
                                                                            {m.instructions.map((ins: string, idx: number) => (
                                                                                <li key={idx}>{ins}</li>
                                                                            ))}
                                                                        </ul>
                                                                    ) : (
                                                                        <span>{m?.instructions ?? "-"}</span>
                                                                    )}
                                                                </div>
                                                                <div>
                                                                    <span className="font-medium">Refills: </span>
                                                                    {m?.refills ?? "-"}
                                                                </div>
                                                                <div>
                                                                    <span className="font-medium">Effective date: </span>
                                                                    {formatActionDate(m?.effective_date)}
                                                                </div>
                                                            </div>
                                                        </div>
                                                    ))}
                                                </div>
                                            )}

                                            {/* Non-medication task details */}
                                            {!isMedicationTask && (
                                                <div className="text-sm text-[#6B7280] bg-white rounded-md p-2 space-y-2">
                                                    <div>
                                                        <span className="font-medium">Price: </span>
                                                        <span>{task?.cost ?? "-"}</span>
                                                    </div>
                                                    {task?.instructions && (
                                                        <div>
                                                            <span className="font-medium">Instructions: </span>
                                                            <span>{task.instructions}</span>
                                                        </div>
                                                    )}
                                                </div>
                                            )}
                                        </div>
                                    );
                                })}
                            </div>
                        ) : (
                            <div className="bg-[#F7F8FA] rounded-[8px] py-12 px-6 flex flex-col items-center justify-center text-center">
                                <p className="font-medium text-[#1A202C] mb-1">No assigned actions</p>
                                <p className="text-sm text-[#6B7280]">
                                    When actions are assigned, they will appear here.
                                </p>
                            </div>
                        )}
                    </div>
                );
            })()}

            {/* Action Buttons */}
            <div className="mt-6 py-4 border-t">
                {program === "weight-loss" && medication?.paymentStatus !== "paid" && (
                    <div className="text-sm text-[#1A202C] bg-[#EBF0FB] p-2 rounded-md flex items-center justify-between mb-2">
                        <div className="flex items-center gap-2">
                            <Info className="text-[#4272DD] size-[16px]" />
                            <span>Payment required before sending to pharmacy.</span>
                        </div>
                        <Button
                            className="!bg-[#2563EB] !text-white !rounded-[6px] !py-1 !px-3 !text-xs"
                            onClick={() => {
                                toast.info('Payment reminder sent to patient');
                            }}
                        >
                            Remind to Pay
                        </Button>
                    </div>
                )}

                <div className="flex gap-3 items-center">
                    {/* Only show reject button for non-batch refills */}
                    {!medication?.isBatch && (
                        <Button
                            className={`w-[133px] !bg-[#E12121] !py-[12px] px-[16px] ${program === "urgent-care" ? "h-[44px] rounded" : "!rounded-[8px]"}`}
                            onClick={() => openRejectModal()}
                        >
                            Reject Refill
                        </Button>
                    )}

                    {program === "urgent-care" ? (
                        <div className="flex-1">
                            <DosespotButton
                                userDosespotId={medication?.patient?.user_dosespot_id ?? ""}
                                sessionId={encounter?.session?.session_id ?? ""}
                                encounterId={encounter?.id ?? ""}
                                onSuccess={() => {}}
                            />
                        </div>
                    ) : (
                        <Button
                            className={cn(
                                "!text-white flex-1 !rounded-[8px] !border-none !py-[14px] !bg-[#4272DD]",
                                program === "weight-loss" && medication?.paymentStatus !== "paid" && "!cursor-not-allowed !opacity-50",
                                shouldDisableSendToPharmacy() && "!cursor-not-allowed !opacity-50"
                            )}
                            onClick={handleApproveRefill}
                            disabled={program === "weight-loss" && medication?.paymentStatus !== "paid" || shouldDisableSendToPharmacy()}
                        >
                            {(approveRefillMutation.isPending || sendInvoiceMutation.isPending) ? (
                                <Loader2 className="w-4 h-4 text-white animate-spin" />
                            ) : (
                                <>
                                    {program === "weight-loss" ? (shouldShowResendText() ? "Resend to Pharmacy" : "Send to Pharmacy") : "Approve Refill"}
                                </>
                            )}
                        </Button>
                    )}
                </div>
            </div>

            {/* Reject Modal */}
            <Dialog open={showRejectModal} onOpenChange={closeRejectModal}>
                <DialogContent className="rounded-[24px]">
                    <DialogHeader className="flex items-center justify-center">
                        <DialogTitle className="text-xl font-medium">Reject Request</DialogTitle>
                    </DialogHeader>
                    <div>
                        <p className="flex items-center mb-4 text-xl font-medium justify-center text-center">
                            You are rejecting the refill request for {rejectingMedication 
                                ? `${rejectingMedication.name} ${rejectingMedication.dosage}` 
                                : medication?.isBatch && Array.isArray(medication?.medications) 
                                    ? `${medication.medications.length} medication(s)` 
                                    : !Array.isArray(medication?.medications) ? `${medication?.medications?.medication || ''} ${medication?.medications?.medicationDosage || ''}` : ''} for {medication?.userName}.
                        </p>
                        <label className="text-[14px] mt-4 text-[#1A202C]">
                            <Select
                                value={rejectReason}
                                onValueChange={setRejectReason}
                            >
                                <SelectTrigger className="w-full rounded-[8px] border h-[44px] p-2 mt-2 mb-4">
                                    <SelectValue placeholder="Select a reason" />
                                </SelectTrigger>

                                <SelectContent>
                                    <SelectGroup>
                                        <SelectItem value="Cannot prescribe this medication">Cannot prescribe this medication</SelectItem>
                                        {program === "urgent-care" && (
                                            <SelectItem value="Need more information">Need more information</SelectItem>
                                            )}
                                        {program === "weight-loss" && (
                                            <SelectItem value="Medication Completed / Discontinued">Medication Completed / Discontinued</SelectItem>
                                        )}
                                    </SelectGroup>
                                </SelectContent>
                            </Select>
                            <label className="text-[14px] mt-4 text-[#1A202C]">Please provide additional details:</label>
                            <textarea
                                value={rejectDetails}
                                onChange={e => setRejectDetails(e.target.value)}
                                maxLength={200}
                                className="w-full rounded-[8px] border h-[164px] p-2 mt-2"
                            />
                        </label>
                        {reasonError && <div className="text-red-500">{reasonError}</div>}
                        <div className="flex justify-end gap-2 mt-4">
                            <Button onClick={closeRejectModal} className="bg-transparent hover:bg-transparent border border-[#DCDCDE] rounded-[8px] text-[#202020CC]">Cancel</Button>
                            <Button
                                className="!bg-[#E12121] rounded-[8px] text-white"
                                disabled={rejectRefillMutation.isPending}
                                onClick={async () => {
                                    if (!rejectReason.trim()) {
                                        setReasonError("Reason is required.");
                                        return;
                                    }
                                    if (!rejectDetails.trim()) {
                                        setReasonError("Additional details are required.");
                                        return;
                                    }
                                    setReasonError("");
                                    await handleRejectRefill(rejectReason, rejectDetails, rejectingRefillId || undefined);
                                    closeRejectModal();
                                }}
                            >
                                {rejectRefillMutation.isPending ? (
                                    <Loader2 className="w-4 h-4 text-white animate-spin" />
                                ) : (
                                    "Confirm"
                                )}
                            </Button>
                        </div>
                    </div>
                </DialogContent>
            </Dialog>

            {/* Image Modal */}
            {selectedImage && (
                <ImageModal
                    isOpen={!!selectedImage}
                    onClose={handleCloseImageModal}
                    imageUrl={selectedImage.url}
                    imageIndex={selectedImage.index}
                    totalImages={medication?.imageUrls?.length || 0}
                />
            )}

            {/* Prescription Modal for weight-loss */}
            {openPrescriptionModal && program === "weight-loss" && encounter?.patient?.id && (
                <PrescriptionModal
                    open={openPrescriptionModal}
                    patientId={encounter.patient.id}
                    encounterId={encounter?.encounterId}
                    onOpenChange={setOpenPrescriptionModal}
                />
            )}

            <MedicalRefillsComponent
                open={isOpen}
                onOpenChange={setIsOpen}
                refillId={medication?.id ?? ""}
            />
        </div>
    );
};
